import useDebounceFn from '@hooks/useDebounceFn';
import { useEffect, useLayoutEffect, useRef, useCallback } from 'react';
/**
 * useInfiniteScroll
 *
 * Triggers onFetchMore when the user scrolls to the top or bottom of the container.
 *
 * @param container - The scrollable element, or null before it mounts. Pass the node itself
 *   rather than a ref: a ref never triggers a re-render, so the listener would be attached
 *   only if the element already existed on the first render.
 * @param onFetchMore - Callback to fetch more items
 * @param hasMore - Whether there are more items to fetch
 * @param isLoading - Whether a fetch is currently in progress
 * @param direction - Direction of infinite scroll ('up' or 'down')
 * @param threshold - How close (in px) to the edge before triggering (default: 100)
 */
function useInfiniteScroll(container, onFetchMore, hasMore, isLoading, direction = 'up', threshold = 100) {
    const anchorRef = useRef(null);
    const fetchingRef = useRef(false);
    const handleScrollFn = useCallback((event) => {
        // Only proceed if it's a user-triggered scroll event
        if (event && !event.isTrusted)
            return;
        if (!container || !hasMore || isLoading || fetchingRef.current)
            return;
        const shouldFetch = direction === 'up'
            ? container.scrollTop <= threshold
            : container.scrollHeight - container.scrollTop - container.clientHeight <= threshold;
        if (shouldFetch) {
            anchorRef.current = { scrollHeight: container.scrollHeight, scrollTop: container.scrollTop };
            fetchingRef.current = true;
            onFetchMore();
        }
    }, [container, onFetchMore, hasMore, isLoading, threshold, direction]);
    const handleScroll = useDebounceFn(handleScrollFn, 100);
    useEffect(() => {
        if (!container)
            return undefined;
        container.addEventListener('scroll', handleScroll);
        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
    }, [container, handleScroll]);
    // Older items are prepended when scrolling up, which shifts the content under the viewport.
    // Restore the previous reading position before the browser paints the taller list.
    useLayoutEffect(() => {
        const anchor = anchorRef.current;
        if (isLoading || !container || !anchor)
            return;
        if (direction === 'up') {
            container.scrollTop = container.scrollHeight - anchor.scrollHeight + anchor.scrollTop;
        }
        anchorRef.current = null;
    }, [isLoading, container, direction]);
    // Reset fetchingRef when loading is done
    useEffect(() => {
        if (!isLoading) {
            fetchingRef.current = false;
        }
    }, [isLoading]);
}
export default useInfiniteScroll;
